import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AirplayIcon from '@mui/icons-material/Airplay';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { Grid, Link, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';

import IconLabel from 'components/common/IconLabel';
import Avatar from 'components/common/Avatar';
import { EventTagType } from 'components/types/event';
import { getIconByAttribute, getIconByAttributeSrc } from 'components/common/editEventIcons';

const maxAttributesLines = 3;

const useStyles = makeStyles()((theme) => ({
  container: {
    margin: theme.spacing(-1, -1.5),
  },
  item: {
    padding: theme.spacing(1, 0, 3, 1.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 1.5),
    },
  },
  showMore: {
    marginTop: theme.spacing(1),
    lineHeight: '19px',
  },
  iconWrapper: {
    padding: 8,
    backgroundColor: '#F5F6FA',
    borderRadius: 8,
  },
}));

function EventAttributes({ type, whatsIncludedTags, isVirtual }) {
  const { classes } = useStyles();
  const theme = useTheme();
  const match = useMediaQuery(theme.breakpoints.down('md'));
  const [t] = useTranslation('public');

  const [isMore, setMore] = useState(false);
  const maxAttributesLength = (match ? maxAttributesLines : maxAttributesLines * 2) - Number(isVirtual);

  const withShowMore = whatsIncludedTags.length > maxAttributesLength;
  const hiddenAttributesLength = whatsIncludedTags.length - maxAttributesLength;

  const handleMoreClick = () => {
    setMore(!isMore);
  };

  let tags = whatsIncludedTags;
  if (withShowMore && !isMore) {
    tags = tags.slice(0, maxAttributesLength);
  }

  return (
    <Grid className={classes.container} container>
      {isVirtual && (
        <Grid className={classes.item} item xs={6} md={4}>
          <IconLabel
            label={t('event.eventSummary.onlineEvent')}
            labelTypographyProps={{ variant: 'body2' }}
            labelUppercase
          >
            <Avatar variant="rounded" small>
              <AirplayIcon />
            </Avatar>
          </IconLabel>
        </Grid>
      )}
      {type && type.parent !== null && (
        <Grid className={classes.item} item xs={6} md={4}>
          <IconLabel label={type.title} labelTypographyProps={{ variant: 'body2' }} labelUppercase>
            <div className={classes.iconWrapper}>
              <Avatar variant="rounded" small>
                <StarBorderRoundedIcon />
              </Avatar>
            </div>
          </IconLabel>
        </Grid>
      )}
      {tags.map(({ id, slug, title }) => (
        <Grid className={classes.item} key={id} item xs={6} md={4}>
          <IconLabel label={title} labelTypographyProps={{ variant: 'body2' }} labelUppercase>
            <div className={classes.iconWrapper}>
              <Avatar variant="square" verySmall src={getIconByAttributeSrc(slug)}>
                {getIconByAttribute(slug)}
              </Avatar>
            </div>
          </IconLabel>
        </Grid>
      ))}
      {withShowMore && (
        <Grid className={classes.item} item xs={12}>
          <Link className={classes.showMore} variant="button" component="button" onClick={handleMoreClick}>
            {isMore ? t('common:button.showLess') : t('common:button.showMore', { count: hiddenAttributesLength })}
          </Link>
        </Grid>
      )}
    </Grid>
  );
}

/* eslint-disable react/forbid-prop-types */
EventAttributes.propTypes = {
  type: PropTypes.object,
  whatsIncludedTags: PropTypes.arrayOf(EventTagType).isRequired,
  isVirtual: PropTypes.bool.isRequired,
};

export default React.memo(EventAttributes);
